/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { Offcanvas, Spinner } from 'react-bootstrap';
import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import AddSettelement from './AddSettelement';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import usePageSearchParams from 'utils/usePageSearchParams';
import Button from 'components/base/Button';
import customerEntityService from 'service/charterer';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import ButtonRefresh from 'components/base/ButtonRefresh';

export type SettelementData = {
  cashier: string;
  currency: string;
  input_balance: string;
  amount: string;
  output_balance: string;
  status: string;
  status_pid: number;
  bg_color: string;
  text_color: string;
  transaction_type: string;
  currency_id: string;
  original_currency_id: string;
  original_currency: string;
  original_amount: string;
  next_states: any;
  guid: string;
  gupid: number;
  date_create: string;
};

const SettelementResultTable = () => {
  const { t, i18n } = useTranslation();
  const [openSettelement, setOpenSettelement] = useState(false);
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);

  const { pathname, state } = useLocation();
  const [searchParams] = usePageSearchParams();

  const { name, remainder } = state;
  const creditor_id = pathname.split('/').pop();
  const currency_id = searchParams.get('currency_id');
  const [totalRemainder, setTotalRemainder] = useState<string>(
    String(remainder)
  );

  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isFetching } = useQuery(
    ['GET_SETTELEMENT_RESULT', currentPage],
    () => {
      if (creditor_id && currency_id) {
        const res = customerEntityService
          .getSettlementProviderById(creditor_id, {
            offset: pageToOffset(currentPage, 10),
            limit: 10,
            currency_id: currency_id
          })
          .then((res: any) => res);
        return res;
      }
    },
    { enabled: (!!creditor_id && !!currency_id) || !!currentPage }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loadingNotActive = isLoading || isFetching;

  const resultData: SettelementData[] = useMemo(() => {
    return (
      data?.debit_credit_settlements.map((items: any) => {
        return {
          cashier:
            (items?.employee?.first_name || '') +
            ' ' +
            (items?.employee?.last_name || ''),
          input_balance: items?.balance_in
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.balance_in)
                .replace(/,/g, ' ')
            : 0,
          output_balance: items?.balance_out
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.balance_out)
                .replace(/,/g, ' ')
            : 0,
          amount: items?.amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.amount)
                .replace(/,/g, ' ')
            : 0,
          status: items?.state_info?.name?.[i18n?.language],
          status_pid: items?.state_info?.pid,
          currency: items?.currency?.name?.[i18n?.language],
          currency_id: items?.currency_id,
          original_currency_id: items?.original_currency_id,
          original_currency: items?.original_currency?.name?.[i18n?.language],
          date_create: items?.date_create,
          original_amount: items?.original_amount,
          creditor_id: items?.creditor_id,
          debitor_id: items?.debitor_id,
          transaction_type: items?.transaction_type,
          provider_indentification_number:
            items?.provider_indentification_number || '',
          provider_name: items?.provider_name || '',
          payment_type: items?.payment_type?.name?.[i18n?.language] || '',
          bg_color: items?.status?.background_color,
          text_color: items?.status?.text_color,
          next_states: items?.next_states || [],
          guid: items?.id,
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const suppliersBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'dt_kt',
      url: '/dtkt/provider'
    },
    {
      label: 'settlement_supplier',
      url: '/dtkt/suppliers'
    },
    {
      label: name ?? '',
      active: true
    }
  ];

  const settelementDataTableColumns: ColumnDef<SettelementData>[] = [
    {
      accessorKey: 'order_number',
      enableSorting: false,
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'cashier',
      header: t('cashier'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),

      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'input_balance',
      header: t('input_balance'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'amount',
      header: t('amounts'),
      cell: rowData => {
        const {
          amount,
          currency_id,
          original_currency_id,
          original_currency,
          original_amount
        } = rowData.row.original;

        return (
          <div className="d-flex gap-2">
            <p className="mb-0">{amount}</p>
            {currency_id !== original_currency_id && (
              <p className="mb-0">
                ( {original_amount + ' ' + original_currency} )
              </p>
            )}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'output_balance',
      header: t('output_balance'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'date_create',
      header: t('created_date'),
      cell: rowData => {
        const { date_create } = rowData?.row?.original || {};
        return (
          <div>
            {date_create
              ? new Date(date_create).toLocaleDateString('ru-RU')
              : ''}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'payment_type',
      header: t('payment_procedure'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'provider_name',
      header: t('firm_name'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },

    {
      accessorKey: 'provider_indentification_number',
      header: t('firm_pnfl'),
      meta: {
        cellProps: { className: 'text-900 ' }
      }
    },
    {
      accessorKey: 'transaction_type',
      header: t('transaction_type'),
      cell: rowData => {
        /* prettier-ignore */
        return (
          <div>
            {rowData?.row?.original?.transaction_type === 'DEBIT'
              ? t('DEBIT')
              : rowData?.row?.original?.transaction_type === 'CREDIT'
                ? t('CREDIT')
                : rowData?.row?.original?.transaction_type === 'RETURN'
                  ? t('RETURN')
                  : t('RECALCULATION')}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status, next_states, gupid, status_pid } =
          rowData?.row?.original || {};

        const [loading, setLoading] = useState({
          confirm: false,
          reject: false
        });
        const confirmState = next_states.find((state: any) =>
          [1, 2, 3, 5, 6, 7].includes(state.pid)
        );
        const rejectState = next_states.find((state: any) => state.pid === 9);

        const statusColors: Record<number, { bg: string; text: string }> = {
          1: { bg: '#e0f7fa', text: '#00796b' },
          2: { bg: '#fffde7', text: '#fbc02d' },
          3: { bg: '#e0f7fa', text: '#00796b' },
          4: { bg: '#fffde7', text: '#fbc02d' },
          5: { bg: '#e8f5e9', text: '#2e7d32' },
          6: { bg: '#e8f5e9', text: '#2e7d32' },
          7: { bg: '#e8f5e9', text: '#2e7d32' },
          8: { bg: '#ffebee', text: '#d32f2f' },
          9: { bg: '#ffebee', text: '#d32f2f' }
        };

        const pid = status_pid;

        const handleSubmit = async () => {
          setLoading(prev => ({ ...prev, confirm: true }));
          try {
            const res = await customerEntityService.updateProposalDebitor(
              gupid,
              {
                cashbox_id: cashboxId,
                cashbox_pid: cashboxPid,
                state: confirmState?.pid
              }
            );
            if (res) {
              refetch();
            }
          } catch (error) {
            console.error('Confirm error:', error);
          } finally {
            setLoading(prev => ({ ...prev, confirm: false }));
          }
        };

        const handleReject = async () => {
          setLoading(prev => ({ ...prev, reject: true }));
          try {
            const res = await customerEntityService.updateProposalDebitor(
              gupid,
              {
                cashbox_id: cashboxId,
                cashbox_pid: cashboxPid,
                state: rejectState?.pid
              }
            );
            if (res) {
              refetch();
            }
          } catch (error) {
            console.error('Reject error:', error);
          } finally {
            setLoading(prev => ({ ...prev, reject: false }));
          }
        };

        return (
          <>
            {next_states?.length > 0 ? (
              <div className="d-flex gap-2">
                {confirmState && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={loading.confirm}
                  >
                    {loading.confirm ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      t('confirm')
                    )}
                  </Button>
                )}

                {rejectState && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleReject}
                    disabled={loading.reject}
                  >
                    {loading.reject ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      t('reject')
                    )}
                  </Button>
                )}
              </div>
            ) : (
              <Button
                style={{
                  backgroundColor: statusColors[pid]?.bg || '#eceff1',
                  color: statusColors[pid]?.text || '#37474f',
                  border: 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                {status}
              </Button>
            )}
          </>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: resultData,
    columns: settelementDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <PageBreadcrumb items={suppliersBreadcrumbItems} />
      <div>
        <div className="mb-5 d-flex justify-content-between">
          <div className="d-flex gap-2 align-items-center">
            <h2>{name ?? ''}</h2>
            <ButtonRefresh onRefresh={() => refetch()} />
          </div>
          {Number(String(totalRemainder).replace(/\s/g, '')) > 0 && (
            <Button variant="primary" onClick={() => setOpenSettelement(true)}>
              {t('add')}
            </Button>
          )}
        </div>

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
            <AdvanceTable
              tableProps={{ className: 'phoenix-table fs-9' }}
              isLoading={loadingNotActive}
            />
            <AdvanceTableCPagination
              count={pageCount}
              page={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>

          <Offcanvas
            show={openSettelement}
            onHide={() => {
              setOpenSettelement(false);
            }}
            placement="end"
            backdrop="static"
            keyboard={false}
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
                {t('mutual_settlement')}
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <AddSettelement
                setOpenSettelement={setOpenSettelement}
                refetch={refetch}
                totalRemainder={totalRemainder}
                setTotalRemainder={setTotalRemainder}
              />
            </Offcanvas.Body>
          </Offcanvas>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default SettelementResultTable;
